/**
 * Hooks for subscribing to real-time task change events via GraphQL.
 *
 * useTaskSubscription  — Kanban board: moves cards between columns when
 *                        a parent task's status changes.
 * useSubtaskSubscription — TaskDialog: refreshes the subtask list when
 *                          any subtask of the open task changes.
 */

import { useEffect, useRef } from "react";
import { useSubscription } from "@apollo/client";
import { TASK_UPDATED_SUBSCRIPTION } from "@/graphql/subscriptions";
import type { TaskStatus } from "@/types";

interface TaskUpdatedEvent {
  taskUpdated: {
    eventType: string;
    taskId: string;
    projectId: string | null;
    oldStatus: string | null;
    newStatus: string;
    version: number;
    title: string;
    updatedByUserId: string | null;
    timestamp: string;
    snapshot: {
      id: string;
      parentId: string | null;
      status: string;
    };
  };
}

/**
 * Kanban board subscription.
 *
 * Listens for parent-task status changes and refetches the affected
 * column(s).  Subtask events are ignored (they don't appear on the board).
 * On reconnect after a network gap, all columns are refetched.
 */
// A Congrid sync can flip many topic statuses in a short burst. Refetching
// the affected column on every individual event would fire a wave of
// concurrent GraphQL requests that contend on the backend DB connection
// pool (and can leave unrelated pages hanging on skeletons). Instead we
// collect the affected statuses arriving within one short window and flush
// them once — at most one refetch per affected column per burst.
const BURST_WINDOW_MS = 300;

export function useTaskSubscription(
  projectIds: string[] | undefined,
  refetchAllColumns: () => void,
  refetchColumn: (status: TaskStatus) => void,
) {
  const wasDisconnected = useRef(false);

  // Statuses awaiting a refetch, plus the single timer that flushes them.
  const pendingStatuses = useRef<Set<TaskStatus>>(new Set());
  const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Hold the latest callbacks in refs so coalescing logic never needs to
  // resubscribe when the parent passes new function identities.
  const refetchColumnRef = useRef(refetchColumn);
  const refetchAllRef = useRef(refetchAllColumns);
  refetchColumnRef.current = refetchColumn;
  refetchAllRef.current = refetchAllColumns;

  const clearPending = () => {
    pendingStatuses.current.clear();
    if (flushTimer.current !== null) {
      clearTimeout(flushTimer.current);
      flushTimer.current = null;
    }
  };

  const flush = () => {
    flushTimer.current = null;
    const statuses = pendingStatuses.current;
    pendingStatuses.current = new Set();
    statuses.forEach((status) => refetchColumnRef.current(status));
  };

  const scheduleColumnRefetch = (status: TaskStatus) => {
    pendingStatuses.current.add(status);
    // Fixed-window coalescing: the first event opens a window, all events
    // within it pile into the set, and one flush fires when it closes.
    if (flushTimer.current === null) {
      flushTimer.current = setTimeout(flush, BURST_WINDOW_MS);
    }
  };

  const { error } = useSubscription<TaskUpdatedEvent>(
    TASK_UPDATED_SUBSCRIPTION,
    {
      variables: { projectIds: projectIds?.length ? projectIds : undefined },
      onError: () => {
        wasDisconnected.current = true;
      },
      onData: ({ data: subscriptionData }) => {
        if (wasDisconnected.current) {
          wasDisconnected.current = false;
          // A full refetch supersedes any queued per-column refetches.
          clearPending();
          refetchAllRef.current();
          return;
        }

        const event = subscriptionData.data?.taskUpdated;
        if (!event) return;

        // Only process parent tasks (subtasks aren't shown on the Kanban).
        if (event.snapshot?.parentId) return;

        const { oldStatus, newStatus } = event;

        if (oldStatus && oldStatus !== newStatus) {
          scheduleColumnRefetch(oldStatus as TaskStatus);
        }
        scheduleColumnRefetch(newStatus as TaskStatus);
      },
    },
  );

  useEffect(() => {
    if (error) {
      console.warn("[TaskSubscription] Error:", error.message);
    }
  }, [error]);

  // Cancel any pending flush on unmount.
  useEffect(() => {
    return () => {
      if (flushTimer.current !== null) {
        clearTimeout(flushTimer.current);
      }
    };
  }, []);
}

/**
 * TaskDialog subtask subscription.
 *
 * Listens for any task events where the changed task's parentId matches
 * the currently open parent task.  When a subtask is updated (by this
 * user or another), the subtask list is refetched so the dialog stays
 * in sync without a manual refresh.
 *
 * Pass `parentId = null` to skip subscribing (dialog closed).
 */
export function useSubtaskSubscription(
  parentId: string | null,
  refetchSubtasks: () => void,
) {
  useSubscription<TaskUpdatedEvent>(
    TASK_UPDATED_SUBSCRIPTION,
    {
      skip: !parentId,
      onData: ({ data: subscriptionData }) => {
        const event = subscriptionData.data?.taskUpdated;
        if (!event) return;

        // Subtask of the open task changed → refresh the list.
        if (event.snapshot?.parentId === parentId) {
          refetchSubtasks();
          return;
        }

        // The parent task itself changed (e.g. derived status) → also
        // refresh so the dialog header reflects the new status.
        if (event.taskId === parentId) {
          refetchSubtasks();
        }
      },
    },
  );
}
